/**
 * components/filters/SearchInput.js — Ерөнхий хайлтын input.
 *
 * Апп бүхэлдээ antd дээр тул энэ нь ч antd `Input` ашиглана. Өмнө нь цэвэр
 * `<input>` байсан бөгөөд antd `Card`/`Row` дотор өндөр, хүрээ, фокусын өнгө
 * нь зөрдөг байв — иймээс хуудас бичигчид дахин ашиглахын оронд бүр болгондоо
 * antd-ээр өөрсдөө бичдэг байсан (31 файл).
 *
 * Props:
 *   value, onChange(value)     — хяналттай утга
 *   placeholder, style, size
 *   allowClear                 — ✕ товч (өгөгдмөл: тийм)
 *   debounce                   — мс. 0 (өгөгдмөл) бол шууд дуудна.
 *
 * ⚠ `debounce`-ыг ЗӨВХӨН серверээс татдаг хайлтад хэрэглэнэ. Аль хэдийн
 *   ачаалагдсан мөрийг client талд шүүж байгаа бол 0 хэвээр — эс бөгөөс
 *   хэрэглэгчид шүүлт хоцорч мэдрэгдэнэ.
 *
 * debounce > 0 үед бичсэн текст ШУУД харагдана (дотоод төлөв), зөвхөн
 * `onChange` дуудалт хойшилно.
 */
import React, { useEffect, useRef, useState } from "react";
import { Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";

export default function SearchInput({
  value,
  onChange,
  placeholder = "Хайх...",
  style,
  // Tailwind-ээр өргөнөө зааж буй хуудсууд бий (жишээ нь "md:w-80") тул
  // класс дамжуулах ёстой. className өгсөн үед flex-ийн өгөгдмөл өргөнийг
  // тавихгүй — эс бөгөөс хоёр эх сурвалж зөрчилдөнө.
  className,
  // ⚠ Өгөгдмөл нь undefined — antd-ийн ConfigProvider-оос ӨВЛӨНӨ.
  //   "middle" гэж тодорхой дамжуулбал хуудсын componentSize="small"-ыг
  //   дарж бичиж, талбар бусад элементээс өндөр харагдана.
  size,
  allowClear = true,
  debounce = 0,
}) {
  // Бичих үед харагдах утга. debounce=0 бол `value`-ийн толь.
  const [text, setText] = useState(value ?? "");
  const timer = useRef(null);
  // Хамгийн сүүлд БИД дээшээ илгээсэн утга. Гаднаас ирсэн өөрчлөлт нь
  // бидний өөрсдийн дуудлагын цуурай мөн эсэхийг ялгахад хэрэглэнэ.
  const emitted = useRef(value ?? "");

  // Гаднаас (жишээ нь «Цэвэрлэх» товчоор) утга солигдвол дагана.
  // ⚠ Хүлээгдэж буй таймерыг цуцлана: хэрэглэгч «abc» бичээд шууд «Цэвэрлэх»
  //    дархад тэр таймер хожим ажиллаж onChange("abc")-г дуудвал цэвэрлэсэн
  //    хайлт буцаад тавигдана. Харин ӨӨРИЙН илгээсэн утгын цуурай бол
  //    хөндөхгүй — эс бөгөөс шинээр бичиж эхэлсэн текст таслагдана.
  useEffect(() => {
    const v = value ?? "";
    if (v === emitted.current) return;
    clearTimeout(timer.current);
    emitted.current = v;
    setText(v);
  }, [value]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const handle = (next) => {
    setText(next);
    if (!debounce) {
      emitted.current = next;
      onChange?.(next);
      return;
    }
    clearTimeout(timer.current);
    timer.current = setTimeout(() => {
      emitted.current = next;
      onChange?.(next);
    }, debounce);
  };

  return (
    <Input
      value={text}
      onChange={(e) => handle(e.target.value)}
      placeholder={placeholder}
      size={size}
      allowClear={allowClear}
      prefix={<SearchOutlined style={{ color: "#A8AFBC" }} />}
      className={className}
      style={
        className
          ? style
          : { flex: "1 1 220px", minWidth: 0, maxWidth: "100%", ...style }
      }
    />
  );
}
